import React from 'react';
import {classes, style} from './doppe-action-type-event-widget.st.css';
import {
    doppeHideableValueGetValue,
    doppeHideableValueIsHidden,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../../client-server-common/types/doppe-hideable-value';
import {CommonClasses, FlexLayout} from '@wix/devzai-common-client';
import {DoppeActionTypeEvent, doppeActionTypeEventResolveSettingsFromBoundEntity} from '../doppe-action-type-event';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    CouponSourceType,
    DoppeActionTypeCouponView
} from '../../../components/doppe-action-type-coupon-view/doppe-action-type-coupon-view';
import {
    DoppeActionTypeWidgetInnerDescription
} from '../../../components/doppe-action-type-widget-inner-description/doppe-action-type-widget-inner-description';
import {
    bymoPageContextPropsResolveBusinessEntity,
    IDoppeViewerActionContext,
    useBymoPageContextProps
} from '../../../doppe-sdk';
import {
    doppeBusinessEntityDtoIsNotFound,
    doppeExternalEventProviderResolveBusinessEntityReference,
    useBymoPageProps
} from '../../../client-server-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    DoppeActionTypeWidgetInnerTitle
} from '../../../components/doppe-action-type-widget-inner-title/doppe-action-type-widget-inner-title';
import {
    dateResolveFromDateTimeValue,
    HtmlObjectResizeMode,
    stringIsNotNullableOrWhiteSpace
} from '@wix/devzai-utils-common';
import {DoppePageMediaView} from '../../../components/doppe-page-media-view/doppe-page-media-view';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {useDoppeViewerLocales} from '../../../client/doppe-viewer-locales/doppe-viewer-locales';
import {
    DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
} from '../../../components/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons/doppe-action-type-widget-content-layout-new-wrapper-sections-and-buttons';
import {
    doppeViewerIntlDateTimeFormatDateString
} from '../../../client-server-common/doppe-viewer/doppe-viewer-intl-utils/doppe-viewer-intl-utils';
import {
    useDoppeViewerLang
} from '../../../components/doppe-viewer-bymo-page-props-react/doppe-viewer-bymo-page-props-react';


export const DoppeActionTypeEventWidget = React.memo(function DoppeActionTypeEventWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeEvent.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            <DoppeActionTypeEventBindableWidget
                actionContext={actionContext}
                reportGoalAchieved={(goalType) => {
                    actionContext.reportGoalAchieved({
                        goalType: goalType,
                    })
                }}
                actionId={actionContext.action.id}
                actionSettings={actionContext.action.settings}
            />
        </DoppeActionTypeWidgetLayout>
    );
});

export const DoppeActionTypeEventBindableWidget = React.memo(function DoppeActionTypeServiceBindableWidget(
    props: {
        actionSettings: DoppeActionTypeEvent.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionContext,
        actionSettings,
        reportGoalAchieved,
        actionId,
        listItemId
    } = props;

    const bymoPageContextProps = useBymoPageContextProps();
    const bymoPageProps = useBymoPageProps();

    const {
        eventProvider,
        eventProviderId
    } = actionSettings;

    if (eventProvider !== null && eventProviderId !== null) {
        const boundService = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalEventProviderResolveBusinessEntityReference(eventProvider, eventProviderId))

        if (!boundService || doppeBusinessEntityDtoIsNotFound(boundService)) {
            return (
                <DoppeActionTypeServiceWidgetContentUnavailable
                    reason={
                        !boundService ?
                            DoppeActionUnavailableReason.ContentLoadingError :
                            DoppeActionUnavailableReason.ContentUnavailable
                    }
                />
            )
        } else {
            return (
                <DoppeActionTypeEventWidgetInner
                    actionContext={actionContext}
                    reportGoalAchieved={reportGoalAchieved}
                    actionId={actionId}
                    actionSettings={{
                        ...actionSettings,
                        ...doppeActionTypeEventResolveSettingsFromBoundEntity(actionSettings, boundService, bymoPageProps),
                    }}
                    listItemId={listItemId}
                />
            )
        }
    } else {
        return (
            <DoppeActionTypeEventWidgetInner
                actionContext={actionContext}
                reportGoalAchieved={reportGoalAchieved}
                actionId={actionId}
                actionSettings={actionSettings}
                listItemId={listItemId}
            />
        )
    }
});

export const DoppeActionTypeEventWidgetInner = React.memo(function DoppeActionTypeEventWidgetInner(
    props: {
        actionSettings: DoppeActionTypeEvent.ActionSettings;
        reportGoalAchieved: (goalType: DoppeActionGoalType) => void;
        actionId: string;
        actionContext: IDoppeViewerActionContext;
        listItemId?: string;
    }
) {
    const {
        actionSettings,
        reportGoalAchieved,
        actionId,
        actionContext,
        listItemId
    } = props;


    const {
        eventLink,
        priceData,
        description,
        eventTitle,
        eventDate,
        eventTime,
        eventImages,
        eventLocation,
        couponCode,
    } = actionSettings;

    const doppeViewerLang = useDoppeViewerLang();

    return (
        <>
            <DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons
                actionContext={actionContext}
                reportGoalAchieved={reportGoalAchieved}
                mainMedia={eventImages && eventImages.length > 0 ? (
                    <DoppePageMediaView
                        className={style(classes.productMedia, {})}
                        mediaResource={eventImages[0]}
                        layoutSpec={{autoHeight: true, maxHeight: 360}}
                        widthSpec={{contentRelativeWidth: 1}}
                        mediaResizeMode={HtmlObjectResizeMode.Cover}
                    />
                ) : null}
                sections={actionSettings.sections}
                buttons={actionSettings.ctaButtons}
                buttonsSettings={{
                    sourceURL: stringIsNotNullableOrWhiteSpace(eventLink) ? eventLink : 'about:blank',
                    listItemId: listItemId,
                }}
            >
                <DoppeActionTypeWidgetInnerTitle>
                    {eventTitle}
                </DoppeActionTypeWidgetInnerTitle>
                {
                    !doppeHideableValueIsHidden(eventDate) || !doppeHideableValueIsHidden(eventTime) || !doppeHideableValueIsHidden(eventLocation) ?
                        <div
                            className={style(classes.eventDetails, {})}
                        >
                            {
                                !doppeHideableValueIsHidden(eventDate) || !doppeHideableValueIsHidden(eventTime) ?
                                    <div
                                        className={style(classes.date, {}, FlexLayout.row.alignToCenter)}
                                    >
                                        {
                                            !doppeHideableValueIsHidden(eventDate) ?
                                                <div
                                                    className={style(classes.eventDate, {}, CommonClasses.ellipsis)}
                                                >
                                                    {doppeViewerIntlDateTimeFormatDateString(
                                                        doppeViewerLang,
                                                        dateResolveFromDateTimeValue(eventDate)
                                                    )}
                                                    {
                                                        !doppeHideableValueIsHidden(eventDate) && !doppeHideableValueIsHidden(eventTime) ?
                                                            ',' : ''
                                                    }
                                                </div>
                                                :
                                                null
                                        }
                                        {
                                            !doppeHideableValueIsHidden(eventTime) ?
                                                <div
                                                    className={style(classes.eventTime, {}, CommonClasses.ellipsis)}
                                                >
                                                    {doppeHideableValueGetValue(eventTime)}
                                                </div>
                                                :
                                                null
                                        }
                                    </div>
                                    :
                                    null
                            }
                            {
                                !doppeHideableValueIsHidden(eventLocation) ?
                                    <div
                                        className={style(classes.eventLocation, {}, CommonClasses.ellipsis)}
                                    >
                                        {doppeHideableValueGetValue(eventLocation)}
                                    </div>
                                    :
                                    null
                            }

                        </div>
                        :
                        null
                }
                {
                    !doppeHideableValueIsHidden(priceData) ?
                        <div
                            className={style(classes.price, {}, CommonClasses.ellipsis)}
                        >
                            {priceData.actualPrice}
                        </div>
                        :
                        null
                }
                <DoppeActionTypeWidgetInnerDescription>
                    {description}
                </DoppeActionTypeWidgetInnerDescription>
                {
                    doppeHideableValueIsVisibleAndNotEqualValue(couponCode, '') ?
                        <DoppeActionTypeCouponView
                            className={style(classes.couponView, {})}
                            coupon={couponCode}
                            couponSourceType={CouponSourceType.Action}
                            actionId={actionId}
                            onCouponCopied={() => {
                                reportGoalAchieved(DoppeActionGoalType.CouponCopied);
                            }}
                        />
                        :
                        null
                }
            </DoppeActionTypeWidgetContentLayoutNewWrapperSectionsAndButtons>
        </>
    );
});


export const DoppeActionTypeServiceWidgetContentUnavailable = React.memo(function DoppeActionTypeServiceWidgetContentUnavailable(
    props: {
        reason: DoppeActionUnavailableReason;
    }
) {
    const {
        reason
    } = props;

    const doppeViewerLocales = useDoppeViewerLocales();

    return (
        <DoppeActionUnavailablePreview
            reason={reason}
            title={doppeViewerLocales['contentUnavailableTitleService']}
        />
    )
});



